<script setup lang="ts">
import { computed, inject, ref, watch } from "vue";
import { useRouter } from "vue-router";
import dayjs from "dayjs";
import { formatNumberForScale } from "chartjs-graph";
import { useBaseStore } from "@/stores/base";
import useDialog from "@/ui/composables/useDialog";
import { useBasePriceStore } from "@/modules/base_price/store";
import { versionsDrawerSourceKey } from "@/modules/base_price/versionsDrawerSource";
import SecondaryDrawerHeader from "@/modules/explorer/components/SecondaryDrawer/SecondaryDrawerHeader.vue";
import BasePricePlanStatus = Modules.BasePrice.Data.BasePricePlanStatus;

/**
 * The version history of the plan being edited, shown in the app's shared
 * secondary drawer (see BaseLayout's #secondary-drawer teleport target).
 *
 * A plan is never edited in place: saving writes a new one carrying the
 * chain's `original_uuid` and archives the rest (see BasePricePlanWriter), so
 * the chain *is* the history and every entry below is still readable exactly
 * as it was saved.
 *
 * Loaded on open rather than with the plan, since this is the only thing that
 * reads it. Keyed on the plan's uuid rather than fetched once on mount: saving
 * moves the editor to the version it just wrote, and the drawer is often open
 * when that happens — without this it would keep showing the chain as it was
 * one version ago.
 */
// The bruto editor provides its own store; without one this is the netto editor's drawer.
const source = inject(versionsDrawerSourceKey, null);
const store = source?.store ?? useBasePriceStore();
const editPath = (uuid: string): string =>
	source?.editPath(uuid) ?? `/base-prices/${uuid}/edit`;
const showBudget = source?.showBudget ?? true;
const draftBooksCount = (): number =>
	source ? 0 : useBasePriceStore().draftBooks.length;
const baseStore = useBaseStore();
const router = useRouter();

watch(() => store.plan?.uuid, store.loadVersions, { immediate: true });

const close = () => (baseStore.showSecondaryDrawer = false);

/** The version the editor currently has open, so it can be marked as "you are here". */
const currentUuid = computed(() => store.plan?.uuid ?? null);

const formatMoment = (value: string | null): string =>
	value ? dayjs(value).format("DD MMM YYYY · HH:mm") : "Never saved";

const statusColor = (status: BasePricePlanStatus): string | undefined => {
	if (status === "open") return "success";
	if (status === "draft") return "warning";

	return undefined;
};

type VersionRow = {
	key: string;
	name: string;
	status: string;
	statusColor: string | undefined;
	target: number;
	booksCount: number;
	authorName: string | null;
	moment: string;
	isDraft: boolean;
	isCurrent: boolean;
	/** Whether reopening it would bring back work that isn't live — see reopen(). */
	isArchived: boolean;
	/**
	 * The stripe down the left, on every row. Its colour says what the row is —
	 * green for the live version, black for history, dashed amber for work that
	 * has not been saved — so it never has to be read as "you are here", which
	 * is what the background tint is for.
	 */
	accent: "draft" | "open" | "archived";
};

/**
 * The work in progress, shown above the version it will be saved from. It is
 * not a plan and has no row in the database — nothing in the editor is written
 * until Save version — so it is built here from the live draft rather than
 * fetched, and it disappears the moment the draft matches what was saved.
 *
 * Whoever is editing is its author by definition: a draft that has never been
 * persisted belongs to this session and nobody else can be looking at it.
 */
const draftRow = computed<VersionRow | null>(() => {
	if (!store.plan || !store.hasUnsavedChanges) return null;

	return {
		key: "draft",
		name: store.plan.name,
		status: "unsaved",
		statusColor: "warning",
		target: store.plan.target,
		booksCount: draftBooksCount(),
		authorName: baseStore.currentUser?.name ?? null,
		moment: "Not saved yet",
		isDraft: true,
		isCurrent: false,
		isArchived: false,
		accent: "draft",
	};
});

const rows = computed<VersionRow[]>(() => [
	...(draftRow.value ? [draftRow.value] : []),
	...store.versions.map(
		(version): VersionRow => ({
			key: version.uuid,
			name: version.name,
			status: version.status,
			statusColor: statusColor(version.status),
			target: version.target,
			booksCount: version.books_count,
			authorName: version.created_by_user?.name ?? null,
			moment: formatMoment(version.created_at),
			isDraft: false,
			isCurrent: version.uuid === currentUuid.value,
			isArchived: version.status === "archived",
			// A plan still in `draft` status has never been saved over, but it is
			// equally not the live version, so it reads as history too.
			accent: version.status === "open" ? "open" : "archived",
		})
	),
]);

const busy = ref(false);

/** The live version of the chain — what discarding a draft falls back to. */
const liveVersion = computed(
	() => store.versions.find((version) => version.status === "open") ?? null
);

/**
 * Swaps the editor onto another version of this chain.
 *
 * The route carries the uuid but the editor reads it once, at setup, so this
 * has to load the plan itself — navigating alone would leave the old one on
 * screen under a new address.
 */
const load = async (uuid: string, asDraft: boolean) => {
	busy.value = true;

	if (uuid !== store.plan?.uuid) {
		await router.replace(editPath(uuid));
	}

	await store.loadPlan(uuid);
	if (asDraft) store.markUnsaved();
	store.loadResolvedPlan(uuid);

	busy.value = false;
};

/**
 * Reopening the live version is just that — it is already the current state,
 * so it loads clean with nothing outstanding. Reopening an archived one brings
 * back contents that are no longer live, so it is marked unsaved: the editor
 * then shows it as a draft, and saving writes a new version on top of whatever
 * is live rather than resurrecting the old row (see
 * BasePricePlanWriter::parentUuidFor, which keeps the chain a single line).
 */
const reopen = async (row: VersionRow) => {
	if (row.isDraft || row.isCurrent || busy.value) return;

	if (store.hasUnsavedChanges) {
		const confirmed = await useDialog().confirm({
			title: "Discard your changes?",
			message:
				"You have unsaved changes to the version you are editing. Opening " +
				"another one leaves them behind.",
			confirmButtonText: "Discard and open",
			isDelete: true,
		});

		if (!confirmed) return;
	}

	await load(row.key, row.isArchived);
};

/**
 * Throws the working copy away and goes back to the live version.
 *
 * Usually that is the plan already on screen and this is a plain reload. After
 * an archived version has been reopened it is a different plan, and going back
 * to the live one is the only way out that doesn't involve saving — which is
 * the case this exists for.
 */
const discardDraft = async () => {
	if (!store.plan || busy.value) return;

	const target = liveVersion.value?.uuid ?? store.plan.uuid;
	const goingElsewhere = target !== store.plan.uuid;

	const confirmed = await useDialog().confirm({
		title: "Discard your changes?",
		message: goingElsewhere
			? `Your unsaved work will be thrown away and "${liveVersion.value?.name}" — the live version — opened instead.`
			: "Your unsaved work will be thrown away and this version reloaded exactly as it was last saved.",
		confirmButtonText: "Discard changes",
		isDelete: true,
	});

	if (!confirmed) return;

	await load(target, false);
};

type RowAction = {
	label: string;
	icon: string;
	danger?: boolean;
	run: () => void;
};

/**
 * What can be done to a row, which is at most one thing. The version you
 * already have open offers nothing — there is nowhere for it to go — so its
 * button is disabled rather than hidden, which keeps every row's timestamp in
 * the same column.
 */
const rowActions = (row: VersionRow): RowAction[] => {
	if (row.isDraft) {
		return [
			{
				label: "Discard changes",
				icon: "fa-light fa-trash-can",
				danger: true,
				run: discardDraft,
			},
		];
	}

	// A deleted version is not coming back: offering to reopen it would put its
	// configuration in front of someone as though it were still a choice.
	if (row.isCurrent || row.status === "deleted") return [];

	return [
		{
			label: row.isArchived ? "Reopen as draft" : "Open",
			icon: "fa-light fa-arrow-rotate-left",
			run: () => reopen(row),
		},
	];
};

/**
 * Two letters standing in for a face. Falls back to a single character rather
 * than an empty circle, since a version with no author still needs the row to
 * line up with the ones that have one.
 */
const initials = (name?: string | null): string => {
	const letters = (name ?? "")
		.split(" ")
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part[0].toUpperCase())
		.join("");

	return letters || "?";
};
</script>

<template>
	<div class="versions-drawer">
		<secondary-drawer-header
			title="Versions"
			icon="fa: fa-light fa-rectangle-list"
			closable
			@close="close"
		/>

		<!--
			The header stays put and only the chain scrolls, so the title and the
			close button are still reachable after a plan with fifty versions has
			been scrolled to the bottom.
		-->
		<perfect-scrollbar
			class="versions-scroll"
			:options="{ suppressScrollX: true }"
		>
			<div v-if="store.loadingVersions" class="d-flex justify-center pa-8">
				<v-progress-circular indeterminate color="primary" size="28" />
			</div>

			<div
				v-else-if="!rows.length"
				class="pa-6 text-body-2 text-medium-emphasis text-center"
			>
				This plan has never been saved, so it has no earlier versions yet.
			</div>

			<div v-else>
				<!--
					Hand-written rows rather than v-list-item: its subtitle is a
					-webkit-box clamped to one line, which silently ate everything after
					the first line of a multi-line entry.
				-->
				<div
					v-for="row in rows"
					:key="row.key"
					class="version-row"
					:class="[
						`version-row--${row.accent}`,
						{ 'version-row--current': row.isCurrent },
					]"
				>
					<!--
						The timestamp heads the row where the name used to: every version
						in a chain carries the same name, so it distinguished nothing and
						cost the line that the one thing telling them apart now has.
					-->
					<div
						class="d-flex align-center ga-2"
						:class="showBudget ? 'mb-2' : 'mb-3'"
					>
						<span class="text-body-2 font-weight-bold text-truncate">
							{{ row.moment }}
						</span>
						<v-spacer />
						<v-chip
							size="x-small"
							label
							variant="tonal"
							:color="row.statusColor"
						>
							{{ row.status }}
						</v-chip>
					</div>

					<div v-if="showBudget" class="d-flex align-center ga-2 mb-3">
						<span class="text-body-2 font-weight-medium">
							{{ formatNumberForScale("money", row.target) }}
						</span>
						<span class="version-dot" />
						<span class="text-caption text-medium-emphasis">
							{{ row.booksCount }} {{ row.booksCount === 1 ? "book" : "books" }}
						</span>
					</div>

					<div class="d-flex align-center ga-2">
						<v-chip size="small" variant="tonal">
							<!-- Same colour as the status chip beside it, so a row reads as one thing. -->
							<v-avatar start :color="row.statusColor ?? 'grey'" size="18">
								<span class="version-initials">
									{{ initials(row.authorName) }}
								</span>
							</v-avatar>
							{{ row.authorName ?? "Unknown" }}
						</v-chip>
						<v-spacer />

						<!--
							Tucked into the row that was already there rather than given a
							row of its own: with ten versions in a chain, a button line per
							entry was most of the drawer's height.
						-->
						<v-menu :disabled="!rowActions(row).length" location="bottom end">
							<template v-slot:activator="{ props: menu }">
								<v-btn
									v-bind="menu"
									icon="fa-light fa-ellipsis-vertical"
									size="x-small"
									variant="text"
									density="comfortable"
									:disabled="busy || !rowActions(row).length"
								/>
							</template>

							<v-list density="compact">
								<v-list-item
									v-for="action in rowActions(row)"
									:key="action.label"
									@click="action.run"
								>
									<template v-slot:prepend>
										<v-icon
											:icon="action.icon"
											size="14"
											:color="action.danger ? 'error' : undefined"
											class="mr-2"
										/>
									</template>
									<v-list-item-title
										class="text-body-2"
										:class="{ 'text-error': action.danger }"
									>
										{{ action.label }}
									</v-list-item-title>
								</v-list-item>
							</v-list>
						</v-menu>
					</div>
				</div>
			</div>
		</perfect-scrollbar>
	</div>
</template>

<style scoped lang="scss">
.versions-drawer {
	display: flex;
	flex-direction: column;
	height: 100%;
}

.versions-scroll {
	// min-height is what actually lets it scroll: without it a flex child grows
	// to fit its content instead of clipping it.
	flex: 1 1 auto;
	min-height: 0;
}

.version-row {
	padding: 14px 16px 14px 13px;
	// Every row carries one; the modifiers below only change its colour, so no
	// row's text sits at a different indent from its neighbours'.
	border-left: 3px solid transparent;
	border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);

	&:last-child {
		border-bottom: none;
	}
}

// The live version of the chain — the one an export would read.
.version-row--open {
	border-left-color: rgb(var(--v-theme-success));
}

// History. `on-surface` rather than a literal black so it stays readable in
// the dark theme, where "black" would disappear into the background.
.version-row--archived {
	border-left-color: rgb(var(--v-theme-on-surface));
}

// Dashed, where a saved version's accent is solid: this row stands for work
// that exists nowhere but this browser tab.
.version-row--draft {
	border-left: 3px dashed rgb(var(--v-theme-warning));
	background: rgba(var(--v-theme-warning), 0.05);
}

// You are here. Kept separate from the stripe so the two say different things:
// the colour is what the version is, the tint is which one you have open.
.version-row--current {
	background: rgba(var(--v-theme-on-surface), 0.04);
}

.version-dot {
	width: 3px;
	height: 3px;
	border-radius: 50%;
	background: rgba(var(--v-theme-on-surface), 0.35);
	flex: 0 0 auto;
}

.version-initials {
	font-size: 9px;
	font-weight: 700;
	line-height: 1;
}
</style>
